'use client';

import * as React from 'react';
import {Box, Divider, Drawer, IconButton, List, ListItem, Typography} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {PersonIcon} from '@/components/ui/icons/PersonIcon';
import Image from 'next/image';
import HeaderNavLink from './HeaderNavLink';
import {LinkWithLink} from '@/components/header/Header';
import {LanguageSwitcher} from '@/components/language-switcher/LanguageSwitcher';
import {StyledNavigationDrawerLanguageSwitcherBox} from '@/components/header/NavigationDrawer.styles';
import {MuiNextLink} from '@/components/MuiNextLink';
import {routeFactory} from '@/routing/route-factory';
import tv4Theme from '@/theme/tv4Theme';

const adminUrl = process.env.NEXT_PUBLIC_SPOTLIGHT_ADMIN_URL;

type NavigationDrawerProps = {
    open: boolean;
    onClose: () => void;
    links: LinkWithLink[];
};

export default function NavigationDrawer({ open, onClose, links }: NavigationDrawerProps) {
  return (
    <Drawer
      anchor='top'
      open={open}
      onClose={onClose}
      id='primary-nav-drawer'
      slotProps={{
        paper: { sx: {
          backgroundColor: tv4Theme.palette.background.white,
          width: '100%',
          borderRadius: `0 0 ${tv4Theme.shape.borderRadiusLg}px ${tv4Theme.shape.borderRadiusLg}px`,
        }},
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 4, pt: 3, pb: 1.5 }}>
        <MuiNextLink
          href={routeFactory.home.root()}
          onClick={onClose}
          style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none', color: 'inherit' }}
        >
          <Image src='/tv4-logo.svg' alt='TV4 Commercial' height={28} width={28} />
          <Typography variant='h4' sx={{ color: 'text.dark' }}>
            TV4 Commercial
          </Typography>
        </MuiNextLink>
        <IconButton onClick={onClose} size='small' sx={{ color: 'text.dark', mr: -1 }} aria-label='Close menu'>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ mx: 4, mb: 1 }} />

      <List sx={{ px: 4, pb: 2, pt: 1 }}>
        {links.map((link) => (
          <ListItem key={link.label} disablePadding sx={{ py: 0.75 }}>
            <HeaderNavLink link={link} isSmallScreen onClose={onClose} />
          </ListItem>
        ))}
      </List>

      <Divider sx={{ mx: 4 }} />

      <StyledNavigationDrawerLanguageSwitcherBox>
        {adminUrl && (
          <IconButton component='a' href={adminUrl} target='_blank' rel='noopener noreferrer' size='small' sx={{ color: 'text.dark' }} aria-label='Admin portal'>
            <PersonIcon />
          </IconButton>
        )}
        <LanguageSwitcher flagSize={24} />
      </StyledNavigationDrawerLanguageSwitcherBox>
    </Drawer>
  );
}